import React from 'react';
import ReactDOM from 'react-dom/client';
import { Amplify } from 'aws-amplify';
import { MantineProvider, createTheme } from '@mantine/core';
import '@mantine/core/styles.css';
import { ConfigProvider as AntConfigProvider, App as AntApp, theme as antTheme } from 'antd';
import ptBR from 'antd/locale/pt_BR';
import App from './App';
import './index.css';
import { registerSW } from './serviceWorkerRegistration';
import { instalarGuardaSessao } from './utils/guardaSessaoInstalar';
import { API_URL } from './config';

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: 'us-east-1_jctYD4qOJ',
      userPoolClientId: 'cl3qhmjhuhkht6tvcmkgt3ak2',
      loginWith: {
        email: true,
      },
    },
  },
});

// Guarda da sessão (07/10/2026): renova pelo tempo corrido (não pelo relógio do
// computador) e, quando uma chamada à API falha por sessão vencida, renova e
// repete — em vez de "Network error" em tudo. Ver utils/guardaSessao.ts.
instalarGuardaSessao(API_URL);

// ─── Tema Mantine (shell — AppShell, sidebar) ────────────────────────
// Mantine continua como base do shell (sidebar + header). As páginas
// internas migram pra Ant Design (mais sério/denso/enterprise pra ERP).
const mantineTheme = createTheme({
  primaryColor: 'brand',
  primaryShade: { light: 7, dark: 5 },
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  headings: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: '600',
  },
  defaultRadius: 'md',
  colors: {
    brand: [
      '#EEF1FF', '#D8DEFC', '#B3BDF8', '#8B9CF3', '#677FED',
      '#4A66E5', '#2E4ED9', '#0930D0', '#0524A4', '#020D7F',
    ],
    cyan: [
      '#E0F7FC', '#B3ECF6', '#80DEF0', '#4DD0E9', '#26C5E4',
      '#02B5DB', '#0299BC', '#017C9A', '#016179', '#004C5E',
    ],
  },
});

// ─── Tema Ant Design (páginas internas) ─────────────────────────────
// Look "ERP serio": denso, cinzas neutros, accent reservada, header de
// tabela claro (nao mais azul forte), bordas finas, sem rounded exagerado.
const antDesignTheme = {
  algorithm: antTheme.defaultAlgorithm,
  token: {
    // Cores Hydrodata
    colorPrimary: '#0930D0',
    colorSuccess: '#0a7f4a',
    colorWarning: '#B07500',
    colorError: '#B91626',
    colorInfo: '#0930D0',
    colorTextBase: '#0E1433',

    // Tipografia (mais densa que o default)
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: 13, // Ant default eh 14 — diminuir 1px aumenta densidade
    fontSizeLG: 14,
    fontSizeSM: 12,

    // Border/Radius enxutos
    borderRadius: 4,
    borderRadiusLG: 6,
    borderRadiusSM: 3,
    lineWidth: 1,

    // Padding compacto
    controlHeight: 30,         // default 32
    controlHeightSM: 24,
    controlHeightLG: 36,
    paddingSM: 10,
    paddingXS: 6,

    // Sombras mais sutis
    boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 1px 6px rgba(0,0,0,0.04)',
    boxShadowSecondary: '0 2px 4px rgba(0,0,0,0.04)',
  },
  components: {
    Table: {
      // Header cinza claro (nao azul forte do Mantine)
      headerBg: '#FAFBFC',
      headerColor: '#0E1433',
      headerBorderRadius: 4,
      // Linhas mais finas
      rowHoverBg: '#F5F7FA',
      cellPaddingBlock: 8,    // default eh 16 — densifica
      cellPaddingInline: 10,
      cellFontSize: 12,
    },
    Button: {
      // Sem sombra colored no primary
      primaryShadow: 'none',
      defaultShadow: 'none',
      controlHeight: 30,
    },
    Input: {
      controlHeight: 30,
    },
    Select: {
      controlHeight: 30,
    },
    Tag: {
      // Tags menores e menos coloridas
      defaultBg: '#F0F1F5',
      defaultColor: '#0E1433',
    },
    Card: {
      // Cards com borda fina, sem hover lift
      headerBg: 'transparent',
      paddingLG: 16,
    },
    Modal: {
      titleFontSize: 14,
    },
    Tabs: {
      // Tabs estilo Linear (line embaixo, texto enxuto)
      titleFontSize: 13,
      horizontalItemGutter: 24,
    },
  },
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <MantineProvider theme={mantineTheme} defaultColorScheme="light">
      <AntConfigProvider theme={antDesignTheme} locale={ptBR}>
        {/* antd App provider: dá contexto pro App.useApp() (message/notification/modal).
            Sem isso, useApp() devolve message=undefined e todo save que chama
            message.error/success estoura DEPOIS de enviar o request (dado salva, modal trava).
            component={false} = sem nó DOM extra, não mexe no layout. */}
        <AntApp component={false}>
          <App />
        </AntApp>
      </AntConfigProvider>
    </MantineProvider>
  </React.StrictMode>
);

// PWA: registra service worker em produção. Não faz nada em localhost
// (pra não atrapalhar o dev loop com cache agressivo).
registerSW();
